/**
 * Payment-transactions ledger API wrapper (v2 — Transaction Ledger, DNKADM-0149 U1).
 *
 * The **money-moved** ledger: `GET /v2/admin/payments/transactions` is the paged,
 * newest-first history of every payment (charges, membership dues, refunds, …) and
 * `GET /v2/admin/payments/revenue-by-location` is the grouped revenue rollup that
 * sits above it. Both are role-gated OWNER/ADMIN, tenant-from-JWT, bearer-only. This
 * is distinct from the Activity log (`api/audit.ts`, action history) and the config
 * feed (`api/configAudit.ts`) — this feed is what *moved*.
 *
 * CONTRACT (dink-api handoff — DNKAPI-0252 shipped + DNKAPI-0280 member/type filters
 * + name enrichment, on `main`/in delivery). Amounts are **major units**
 * (`total: 50.00`) — `formatMoney` renders them directly. A **refund** is its own row
 * (`kind:'refund'`, `parentProviderRef` → the original charge, usually
 * `status:'refunded'`).
 *
 * **Forward-compat link field (DNKAPI-0281, landing shortly):** each row will
 * additionally carry a best-effort **`reservationUuid`** (null when unresolvable).
 * It is parsed NULL-SAFE now — present → the value, absent → `null` — so the
 * reservation deep-link (`/reservations?reservation=<uuid>`, DNKADM-0147) lights up
 * automatically once the field ships; until then the row shows `reservationId` as
 * plain text.
 *
 * **Forward-compat order link (dink-api 2026-08-04, DNKADM-0251):** the row will
 * additionally carry a nullable **`orderId`** (populated when the transaction belongs
 * to an order, null otherwise) and the new **`kind: 'order'`** for package/credit
 * sales. Both are parsed NULL-SAFE/tolerantly now — an absent `orderId` degrades to
 * `null`, which hides the ledger's Refund/Void action exactly as today — so the
 * affordance lights up automatically once the field ships. `providerRef` is
 * deliberately NOT parsed for this linkage (its shape is pay-api's, not ours).
 *
 * **Enrichment is best-effort:** `contactName`/`contactEmail` may be null (the raw
 * `contactId` is the fallback) — a null name is NEVER an error.
 *
 * Paging is **0-INDEXED on the wire** (first page = `page=0`). The UI keeps a
 * 1-indexed page; `toWirePage`/`toUiPage` (`api/paging.ts`, DNKADM-0148) are the one
 * canonical conversion — sending `?page=1` first would skip 0-indexed page 0.
 *
 * Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049): bearer
 * token, NO CSRF, NO cookies, the bounded 401→refresh→retry.
 *
 * DEFENSIVE PARSE — mirrors `api/configAudit.ts` / `api/members.ts`: each scalar
 * field is read defensively, a missing/wrong-typed field degrades to `null` (never
 * throws), numeric amounts default to `0`, a row with no `id` is dropped, and a
 * non-array payload yields an empty feed.
 */
import { request } from './client';
import { toWirePage, toUiPage } from './paging';
import { parseDeclineReason, type DeclineReason } from '@/utils/declineReason';

/**
 * The transaction kind (DNKAPI-0252; `order` added by the dink-api 2026-08-04
 * contract). Carried as a plain string so an unrecognized upstream kind still
 * renders (we never throw on an unknown value); the known set documents the
 * contract for the type filter.
 *
 * `order` replaces `other` for package/credit sales — ONE value for the whole sale,
 * not per-line (one transaction covers the whole cart). dink-api's precedence stays
 * `refund > membership > reservation > order > other`, so a membership sale still
 * reports `membership`. Nothing here pins `other`: the ledger's Type filter defaults
 * to "All types" and sends `kind` only when a specific type is chosen.
 */
export type PaymentTransactionKind =
  | 'reservation'
  | 'membership'
  | 'refund'
  | 'order'
  | 'other'
  | (string & {});

/** The known payment kinds, in a stable display/filter order. */
export const PAYMENT_TRANSACTION_KINDS: readonly PaymentTransactionKind[] = [
  'reservation',
  'membership',
  'refund',
  'order',
  'other',
];

/**
 * The transaction status (DNKAPI-0252). Carried as a plain string so an
 * unrecognized upstream status still renders; the known set documents the contract
 * for the status filter.
 */
export type PaymentTransactionStatus =
  | 'pending'
  | 'succeeded'
  | 'failed'
  | 'requires_action'
  | 'requires_redirect'
  | 'refunded'
  | (string & {});

/** The known payment statuses, in a stable display/filter order. */
export const PAYMENT_TRANSACTION_STATUSES: readonly PaymentTransactionStatus[] = [
  'pending',
  'succeeded',
  'failed',
  'requires_action',
  'requires_redirect',
  'refunded',
];

/**
 * One parsed payment-transaction row. Every enrichment/optional field may be `null`
 * on the wire; amounts default to `0`; `reservationUuid` is the DNKAPI-0281
 * forward-compat link field (present → value, absent → `null`).
 */
export interface PaymentTransactionRow {
  /** The transaction id — the row key. A row with no id is dropped (never null here). */
  id: string;
  /** The PSP/provider name (e.g. `stripe`), or null. */
  provider: string | null;
  /** The provider's reference for this transaction, or null. */
  providerRef: string | null;
  /** For a refund, the original charge's provider ref (`kind:'refund'`), or null. */
  parentProviderRef: string | null;
  /** The member's external contact id — DATA, never surfaced as "GHL …". */
  contactId: string | null;
  /** Server-enriched contact name (best-effort), or null (fall back to `contactId`). */
  contactName: string | null;
  /** Server-enriched contact email (best-effort), or null. */
  contactEmail: string | null;
  /** `reservation | membership | refund | order | other`, or the raw string when unrecognized. */
  kind: PaymentTransactionKind | null;
  /**
   * The order this transaction belongs to, or null when it has none (a reservation
   * charge, a standalone member purchase). Non-null is the ONLY signal that an
   * in-ledger reversal is possible (dink-api ruling, 2026-08-04) — the reversal
   * endpoints are order-scoped and `providerRef` is deliberately NOT parsed for this.
   * Absent/unparseable → `null`, never `0`, so the action stays hidden until the
   * field ships.
   */
  orderId: number | null;
  /** The 0056 membership-plan id this payment relates to, or null. */
  membershipPlanId: string | null;
  /** The reservation id this payment relates to (plain text link target), or null. */
  reservationId: string | null;
  /**
   * Best-effort reservation UUID (DNKAPI-0281, forward-compat). Present → the value
   * for the `/reservations?reservation=<uuid>` deep link; absent/unresolvable → null
   * (the row shows `reservationId` as plain text instead).
   */
  reservationUuid: string | null;
  /** Gross amount, MAJOR units (`formatMoney`). Defaults 0. */
  gross: number;
  /** Tax amount, MAJOR units. Defaults 0. */
  tax: number;
  /** Total amount, MAJOR units. Defaults 0. */
  total: number;
  /** ISO-4217 currency code (drives `formatMoney`), or null. */
  currency: string | null;
  /** `pending | succeeded | failed | …`, or the raw string when unrecognized. */
  status: PaymentTransactionStatus | null;
  /** On a `failed` row, the provider failure code, or null. */
  failureCode: string | null;
  /**
   * On a declined card charge, why it was declined (DNKAPI-0676), or null when unknown, not a
   * decline, or a value this build does not know. `@/utils/declineReason` gives the line.
   */
  declineReason?: DeclineReason | null;
  /** The location the payment was requested at, or null. */
  requestedLocationRef: string | null;
  /** ISO `createdAt` (WHEN) — newest-first sort + display, or null. */
  createdAt: string | null;
  /**
   * The linked order's payment mode (DNKAPI-0467, pinned to the fleet's
   * lowercase wire value by DNKAPI-0468): `card` (a real PSP charge), `comp`
   * (free — no money taken), `external` (tendered outside dink — recorded,
   * never charged). `null` when the row has no order, the order didn't
   * resolve, or the best-effort enrichment failed. A DISPLAY HINT only
   * (DNKADM-0305): it picks which reversal verb the ledger offers —
   * `comp` → Void, `card`/`external` → Refund, `null` → both (as before) —
   * while `OrderReversalService` stays the sole server-side authority.
   */
  orderPaymentMode: OrderPaymentMode | null;
  /**
   * DNKAPI-0658 (DNKADM-0452): a refund of this charge is in flight. Only a charge row can be
   * true. Missing on an older API (reads as not in progress).
   */
  refundInProgress?: boolean | undefined;
  /**
   * The in-flight refund’s amount, MAJOR units, or null when unknown (a refund recorded
   * before the amount was stored). Never shown as $0.00.
   */
  refundInProgressAmount?: number | null | undefined;
}

/** The linked order's payment mode — the fleet's pinned lowercase values. */
export type OrderPaymentMode = 'card' | 'comp' | 'external';

/** Parsed `GET /v2/admin/payments/transactions` page (standard Spring `Page` envelope). */
export interface PaymentTransactionsPage {
  items: PaymentTransactionRow[];
  page: number;
  size: number;
  totalElements: number | null;
  totalPages: number | null;
}

/**
 * One grouped revenue total (`GET /v2/admin/payments/revenue-by-location`) — totals
 * grouped by location × currency × status. Every dimension may be null on the wire;
 * `total` defaults to 0.
 */
interface RevenueGroup {
  /** The location the revenue was booked at, or null (tenant-wide / unspecified). */
  locationRef: string | null;
  /** ISO-4217 currency code (drives `formatMoney`), or null. */
  currency: string | null;
  /** The payment status the group aggregates, or null. */
  status: string | null;
  /** The grouped total, MAJOR units (`formatMoney`). Defaults 0. */
  total: number;
}

/**
 * Parsed revenue rollup — a flat list of the {@link RevenueGroup}s that arrived.
 * Kept deliberately shape-tolerant: the server may publish the groups under a
 * `groups`/`content`/`results`/`data` wrapper or as a bare array; whatever groups
 * arrive are rendered.
 */
export interface RevenueByLocation {
  groups: RevenueGroup[];
}

// --- Defensive parsing helpers (mirror api/configAudit.ts) ---------------------

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): v2 entity fk ids may
 * arrive as numbers and are normalized to the `string` the UI keys off. Used for
 * nested entity-reference ids (`membershipPlanId`, `reservationId`); the member's
 * OWN identity (`contactId`) stays {@link asText} — never coerced.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A finite number (accepts numeric strings, e.g. a BigDecimal serialized oddly), or null. */
function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** A usable v2 entity id (always positive), or null — `0`/negative is never a real id. */
function positiveId(value: number | null): number | null {
  return value !== null && value > 0 ? value : null;
}

/**
 * Parse the order payment mode tolerantly (DNKADM-0305): the contract is the
 * pinned LOWERCASE value (DNKAPI-0468), but the field's one pre-consumption
 * correction was exactly a casing fix (0467 briefly shipped `CARD`), so the
 * case is normalized at this boundary — an uppercase value must not silently
 * disable the verb narrowing. An unknown value degrades to `null` (both verbs
 * offered, the pre-0305 behaviour), never a throw.
 */
function asOrderPaymentMode(value: unknown): OrderPaymentMode | null {
  const text = asText(value)?.toLowerCase() ?? null;
  return text === 'card' || text === 'comp' || text === 'external' ? text : null;
}

/**
 * Parse one raw transaction row DEFENSIVELY. Returns `null` for a row with NO usable
 * `id` (it can't be keyed or linked — the caller drops it). Numeric amounts default
 * to `0`; every other field degrades to `null` rather than throwing.
 */
function parseTransaction(raw: unknown): PaymentTransactionRow | null {
  const o = isObject(raw) ? raw : {};
  // The row's own id is a genuine transaction id — asId tolerates a numeric wire id.
  const id = asId(o['id']) ?? asId(o['transactionId']);
  if (id === null) return null; // no id ⇒ not renderable — drop it
  return {
    id,
    provider: asText(o['provider']),
    providerRef: asText(o['providerRef']),
    parentProviderRef: asText(o['parentProviderRef']),
    // The contact's OWN identity is a genuine external id — asText, never coerced.
    contactId: asText(o['contactId']),
    contactName: asText(o['contactName']),
    contactEmail: asText(o['contactEmail']),
    kind: asText(o['kind']),
    // dink-api 2026-08-04 forward-compat: absent/unparseable → null (never 0, which
    // would offer a reversal against a non-existent order). A v2 entity id is always
    // positive, so a 0/negative sentinel degrades to null too (mirrors parseCompValue).
    orderId: positiveId(asNumber(o['orderId'])),
    membershipPlanId: asId(o['membershipPlanId']) ?? asId(o['planId']),
    reservationId: asId(o['reservationId']),
    // DNKAPI-0281 forward-compat: present → value, absent → null (never throws).
    reservationUuid: asText(o['reservationUuid']),
    gross: asNumber(o['gross']) ?? 0,
    tax: asNumber(o['tax']) ?? 0,
    total: asNumber(o['total']) ?? 0,
    currency: asText(o['currency']),
    status: asText(o['status']),
    failureCode: asText(o['failureCode']),
    declineReason: parseDeclineReason(o['declineReason']),
    requestedLocationRef: asText(o['requestedLocationRef']) ?? asText(o['locationRef']),
    createdAt: asText(o['createdAt']),
    orderPaymentMode: asOrderPaymentMode(o['orderPaymentMode']),
    refundInProgress: o['refundInProgress'] === true,
    refundInProgressAmount: asNumber(o['refundInProgressAmount']),
  };
}

/**
 * Extract the rows array defensively — the live Spring `Page` publishes rows under
 * `content`; `items`/`results`/`data` are tolerated as fallbacks; a bare array is
 * accepted. A non-array (or missing) field yields an empty list. Rows with no `id`
 * are dropped.
 */
function parseTransactions(body: Record<string, unknown>): PaymentTransactionRow[] {
  const candidates = [body['content'], body['items'], body['results'], body['data']];
  const arr =
    candidates.find((c): c is unknown[] => Array.isArray(c)) ??
    (Array.isArray(body) ? (body as unknown[]) : null);
  if (!arr) return [];
  return arr
    .map(parseTransaction)
    .filter((r): r is PaymentTransactionRow => r !== null);
}

/** Parse an epoch-or-ISO timestamp to a sortable number; unknown → -Infinity (sorts last). */
function timestampOrder(ts: string | null): number {
  if (ts === null) return Number.NEGATIVE_INFINITY;
  const ms = Date.parse(ts);
  if (Number.isFinite(ms)) return ms;
  const num = Number(ts);
  return Number.isFinite(num) ? num : Number.NEGATIVE_INFINITY;
}

/**
 * Re-sort newest-first by `createdAt`. The endpoint is documented as already
 * newest-first, but we re-sort client-side defensively (mirrors configAudit) so a
 * server that returns a different order still renders correctly. Rows with no
 * parseable timestamp sort to the end, preserving their relative order (stable).
 */
function sortNewestFirst(rows: PaymentTransactionRow[]): PaymentTransactionRow[] {
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => {
      const diff = timestampOrder(b.r.createdAt) - timestampOrder(a.r.createdAt);
      return diff !== 0 ? diff : a.i - b.i;
    })
    .map(({ r }) => r);
}

function parsePage(
  body: unknown,
  fallbackPage: number,
  fallbackSize: number,
): PaymentTransactionsPage {
  const o = isObject(body) ? body : {};
  return {
    items: sortNewestFirst(parseTransactions(o)),
    page: asNumber(o['page']) ?? fallbackPage,
    size: asNumber(o['size']) ?? asNumber(o['pageLimit']) ?? fallbackSize,
    // The live Spring `Page` publishes the count as `totalElements`; `total` is a legacy fallback.
    totalElements: asNumber(o['totalElements']) ?? asNumber(o['total']),
    totalPages: asNumber(o['totalPages']),
  };
}

/** Parse one raw revenue group defensively (`total` defaults 0; dimensions may be null). */
function parseRevenueGroup(raw: unknown): RevenueGroup {
  const o = isObject(raw) ? raw : {};
  return {
    locationRef: asText(o['locationRef']) ?? asText(o['requestedLocationRef']),
    currency: asText(o['currency']),
    status: asText(o['status']),
    total: asNumber(o['total']) ?? 0,
  };
}

/**
 * Parse the revenue rollup defensively. Accepts a bare array, or a
 * `groups`/`content`/`results`/`data` wrapper; a non-array (or missing) field yields
 * an empty group list rather than throwing — render whatever groups arrive.
 */
function parseRevenueByLocation(body: unknown): RevenueByLocation {
  let list: unknown[] | null = null;
  if (Array.isArray(body)) list = body;
  else if (isObject(body)) {
    const candidates = [body['groups'], body['content'], body['results'], body['data']];
    list = candidates.find((c): c is unknown[] => Array.isArray(c)) ?? null;
  }
  return { groups: (list ?? []).map(parseRevenueGroup) };
}

// --- Endpoints -----------------------------------------------------------------

export interface ListPaymentTransactionsParams {
  /** 1-indexed UI page; converted to the 0-indexed wire page. Defaults 1. */
  page?: number;
  /** Page size (default 20, max 100). */
  size?: number;
  /** Member filter (external contact id); sent as `?contactId=` ONLY when set. */
  contactId?: string;
  /** Type filter; sent as `?kind=` ONLY when set. */
  kind?: PaymentTransactionKind | string;
  /** Status filter; sent as `?status=` ONLY when set. */
  status?: PaymentTransactionStatus | string;
  /** Location filter; sent as `?locationRef=` ONLY when set. */
  locationRef?: string;
  /** Inclusive lower bound (`yyyy-MM-dd`); sent as `?from=` ONLY when set. */
  from?: string;
  /** Inclusive upper bound (`yyyy-MM-dd`); sent as `?to=` ONLY when set. */
  to?: string;
}

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;

/**
 * `GET /v2/admin/payments/transactions?page&size[&contactId=&kind=&status=&locationRef=&from=&to=]`
 * → a parsed, newest-first page of the money-moved ledger. Defaults `page=1`,
 * `size=20`. Each filter is sent ONLY when set (omitting a filter widens the feed).
 * The 1-indexed UI page is sent as `toWirePage(page)` so the first UI page (1) maps
 * to wire `?page=0` (DNKADM-0148); the returned 0-indexed page maps back to 1-indexed
 * via `toUiPage`. Bearer-only `/v2/admin/**` (no CSRF, no cookies). Throws `ApiError`
 * on a non-2xx response.
 */
export async function listPaymentTransactions(
  params: ListPaymentTransactionsParams = {},
): Promise<PaymentTransactionsPage> {
  const page = params.page ?? DEFAULT_PAGE;
  const size = params.size ?? DEFAULT_PAGE_SIZE;
  const contactId = asText(params.contactId) ?? undefined;
  const kind = asText(params.kind) ?? undefined;
  const status = asText(params.status) ?? undefined;
  const locationRef = asText(params.locationRef) ?? undefined;
  const from = asText(params.from) ?? undefined;
  const to = asText(params.to) ?? undefined;
  const body = await request<unknown>('GET', '/v2/admin/payments/transactions', {
    query: { page: toWirePage(page), size, contactId, kind, status, locationRef, from, to },
  });
  const parsed = parsePage(body, toWirePage(page), size);
  // Remap the backend's 0-indexed page back to the 1-indexed UI page (mirrors listConfigAudit).
  return { ...parsed, page: toUiPage(parsed.page) };
}

export interface RevenueByLocationParams {
  /** Inclusive lower bound (`yyyy-MM-dd`); sent as `?from=` ONLY when set. */
  from?: string;
  /** Inclusive upper bound (`yyyy-MM-dd`); sent as `?to=` ONLY when set. */
  to?: string;
}

/**
 * `GET /v2/admin/payments/revenue-by-location?from=&to=` → the parsed revenue rollup
 * (totals grouped by location × currency × status) for the current date window. Each
 * bound is sent ONLY when set. Parsed defensively — render whatever groups arrive.
 * Bearer-only `/v2/admin/**`. Throws `ApiError` on a non-2xx response.
 */
export async function getRevenueByLocation(
  params: RevenueByLocationParams = {},
): Promise<RevenueByLocation> {
  const from = asText(params.from) ?? undefined;
  const to = asText(params.to) ?? undefined;
  const body = await request<unknown>('GET', '/v2/admin/payments/revenue-by-location', {
    query: { from, to },
  });
  return parseRevenueByLocation(body);
}
